import { useState, useEffect } from "react";
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "@/config/firebase";
import {
  ADMIN_ROLE_CLAIM,
  ADMIN_ROLE_VALUE,
  COLLECTIONS,
} from "@/config/constants";

interface AuthState {
  user: FirebaseUser | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for authentication and admin role checking
 */
export const useAuth = () => {
  const allowDevAuthBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAdmin: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Check for admin role in custom claims (preferred method)
          const tokenResult = await user.getIdTokenResult();
          let isAdmin =
            tokenResult.claims[ADMIN_ROLE_CLAIM] === ADMIN_ROLE_VALUE;

          // Fallback: Check Firestore if custom claim is not set
          if (!isAdmin) {
            try {
              const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
              if (userDoc.exists()) {
                const userData = userDoc.data() as { profile?: { role?: string }; role?: string };
                isAdmin =
                  userData?.profile?.role === ADMIN_ROLE_VALUE ||
                  userData?.role === ADMIN_ROLE_VALUE;

                if (isAdmin) {
                  console.warn(
                    "⚠️ Admin role found in Firestore, but not in Custom Claims. " +
                      "For production, set Custom Claims via Cloud Function."
                  );
                }
              }
            } catch (fsError) {
              console.warn("⚠️ Admin-Check über Firestore fehlgeschlagen:", fsError);
              if (allowDevAuthBypass) {
                console.warn("⚠️ Admin-Check konnte nicht verifiziert werden (DEV-Fallback aktiv).");
                isAdmin = true;
              }
            }
          }

          setAuthState({
            user,
            isAdmin,
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error("Error checking admin status:", error);
          setAuthState({
            user,
            isAdmin: false,
            loading: false,
            error: "Fehler beim Prüfen der Berechtigungen",
          });
        }
      } else {
        setAuthState({
          user: null,
          isAdmin: false,
          loading: false,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, [allowDevAuthBypass]);

  /**
   * Sign in with email and password
   */
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Fehler beim Anmelden";
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  /**
   * Sign out
   */
  const signOut = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  return {
    ...authState,
    signIn,
    signOut,
  };
};
